import { CONNECTIONS_SOURCE, CONNECTIONS_LAYER } from './constants';
import { safeSetSourceData } from './utils';


/**
 * Convert connection data to GeoJSON format
 * @param {Array} connections - Array of connections
 * @returns {Object} GeoJSON FeatureCollection
 */
const connectionsToGeoJSON = (connections) => {
  if (!connections || connections.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  const features = [];

  connections.forEach(connection => {
    if (!connection.points || connection.points.length !== 2) {
      return;
    }

    const [point1, point2] = connection.points;
    
    // Validate point data
    if (!point1 || !point2 || 
        typeof point1.latitude !== 'number' || 
        typeof point1.longitude !== 'number' ||
        typeof point2.latitude !== 'number' || 
        typeof point2.longitude !== 'number' ||
        isNaN(point1.latitude) || isNaN(point1.longitude) ||
        isNaN(point2.latitude) || isNaN(point2.longitude)) {
      return;
    }
    
    // Create a single line feature (no parallel lines)
    const feature = {
      type: 'Feature',
      properties: {
        connectionId: connection.id,
        transportMode: connection.transportMode,
        color: connection.color,
        point1Name: point1.name,
        point2Name: point2.name
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          [point1.longitude, point1.latitude],
          [point2.longitude, point2.latitude]
        ]
      }
    };
    
    features.push(feature);
  });

  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Initialize connections layer
 * @param {Object} map - Mapbox map instance
 */
export const initializeConnectionsLayer = (map) => {
  if (!map) {
    return;
  }
  
  // Wait for map to be ready if it's not loaded yet
  if (!map.loaded()) {
    // Use once to avoid duplicate event listeners
    map.once('load', () => {
      initializeConnectionsLayer(map);
    });
    return;
  }

  try {
    // Add connections data source
    if (!map.getSource(CONNECTIONS_SOURCE)) {
      map.addSource(CONNECTIONS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    // Remove existing layer if it exists to ensure clean recreation
    if (map.getLayer(CONNECTIONS_LAYER)) {
      map.removeLayer(CONNECTIONS_LAYER);
    }
    
    // Try to insert before points layer, or add on top if points layer doesn't exist
    let beforeLayerId = null;
    const existingLayers = map.getStyle().layers;
    
    // Look for points circle layer to insert before it
    const pointsLayer = existingLayers.find(layer => layer.id === 'points-circle-layer');
    if (pointsLayer) {
      beforeLayerId = pointsLayer.id;
    }
    
    // Always add fresh layer with correct styling
    map.addLayer({
      id: CONNECTIONS_LAYER,
      type: 'line',
      source: CONNECTIONS_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        'visibility': 'visible'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 4,
        'line-opacity': 0.8,
        'line-dasharray': [6, 10]
      }
    }, beforeLayerId);
  } catch (error) {
    console.error('Error initializing connections layer:', error);
  }
};

/**
 * Update connections display on map
 * @param {Object} params - Parameters object
 * @param {Object} params.map - Mapbox map instance
 * @param {boolean} params.mapReady - Whether map is ready
 * @param {boolean} params.sourcesReady - Whether data sources are ready
 * @param {Array} params.connections - Array of connections
 */
export const updateConnections = ({
  map,
  mapReady,
  sourcesReady,
  connections
}) => {
  if (!map || !mapReady || !sourcesReady) {
    return;
  }

  try {
    // Ensure connections layer is initialized
    initializeConnectionsLayer(map);

    // Convert connection data to GeoJSON format
    const connectionsData = connectionsToGeoJSON(connections);

    // Update data source
    safeSetSourceData(map, mapReady, sourcesReady, CONNECTIONS_SOURCE, connectionsData);

    // Handle layer visibility based on connections
    if (map.getLayer(CONNECTIONS_LAYER)) {
      if (connections && connections.length > 0) {
        map.setLayoutProperty(CONNECTIONS_LAYER, 'visibility', 'visible');
      } else {
        // Hide layer when no connections to ensure clean map
        map.setLayoutProperty(CONNECTIONS_LAYER, 'visibility', 'none');
      }
    }

  } catch (error) {
    console.error('Error updating connections:', error);
  }
};

/**
 * Clear all connections
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @param {boolean} sourcesReady - Whether data sources are ready
 */
export const clearConnections = (map, mapReady, sourcesReady) => {
  updateConnections({
    map,
    mapReady,
    sourcesReady,
    connections: []
  });
};
